import { useMemo } from 'react';
import { useQuery, useQueryClient } from 'react-query';

import { type AccountingSoftware } from 'modules/bookkeep/integration/status';
import { getV2ApiCompanyIdAccountingIntegrationsAccountingSoftware } from 'src/client/spendesk-api';

import { useCompanyId } from '../../app/hooks/useCompanyId';

const accountingSoftwareNameMap = new Map<AccountingSoftware, string>([
  ['Netsuite', 'NetSuite'],
  ['Quickbooks', 'QuickBooks Online'],
  ['Datev', 'DATEV'],
  ['MicrosoftDynamics', 'Microsoft Business Central'],
  ['Sage100', 'Sage 100'],
  ['ExactOnlineBe', 'Exact Online BE'],
  ['ExactOnlineFr', 'Exact Online FR'],
  ['ExactOnlineNl', 'Exact Online NL'],
]);

export const useAccountingSoftwareName = () => {
  const companyId = useCompanyId();
  const { data, isLoading } = useQuery({
    queryKey: ['accounting-software', companyId],
    staleTime: Number.POSITIVE_INFINITY,
    queryFn: async () => {
      const response =
        await getV2ApiCompanyIdAccountingIntegrationsAccountingSoftware({
          path: {
            companyId,
          },
        });

      if (response.statusCode === 200 && response.body.status === 'connected') {
        return response.body.accountingSoftware;
      }

      return undefined;
    },
  });

  const accountingSoftwareName = useMemo(() => {
    // TODO - use translations?
    if (!data) {
      return undefined;
    }
    const value = accountingSoftwareNameMap.get(data);
    return value ?? data;
  }, [data]);

  return {
    integration: data,
    accountingSoftwareName,
    isLoading,
  };
};

export const useInvalidateAccountingSoftwareQuery = () => {
  const companyId = useCompanyId();
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries({
      queryKey: ['accounting-software', companyId],
    });
  };
};
